import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Check,
  Home,
  Info,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Navigation,
  Pencil,
  Phone,
  Plus,
  Save,
  Store,
  Trash2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";

const emptyAddress = {
  label: "",
  address: "",
  is_default: false,
  lat: null,
  lng: null,
};

function AddressIcon({ label, className = "h-5 w-5" }) {
  if (/بيت|منزل/.test(label)) return <Home className={className} />;
  if (/محل|عمل|مكتب/.test(label)) return <Store className={className} />;
  return <MapPin className={className} />;
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, refreshUser, updateProfile } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [addresses, setAddresses] = useState([]);
  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [editingId, setEditingId] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [locating, setLocating] = useState(false);
  const [activeSection, setActiveSection] = useState("profile");
  const [addressFormOpen, setAddressFormOpen] = useState(false);

  useEffect(() => {
    setProfile({ name: user?.name || "", phone: user?.phone || "" });
  }, [user?.name, user?.phone]);

  const loadAddresses = async () => {
    try {
      const { data } = await api.get("/profile/addresses");
      setAddresses(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoadingAddresses(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, []);

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!profile.name.trim()) {
      toast.error("الاسم مطلوب");
      return;
    }
    setSavingProfile(true);
    try {
      await updateProfile(profile);
      toast.success("تم حفظ معلوماتك");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const resetAddressForm = () => {
    setAddressForm(emptyAddress);
    setEditingId(null);
    setAddressFormOpen(false);
  };

  const saveAddress = async (event) => {
    event.preventDefault();
    if (!addressForm.label.trim() || !addressForm.address.trim()) {
      toast.error("اكتب اسم العنوان وتفاصيله");
      return;
    }
    setSavingAddress(true);
    try {
      if (editingId) {
        await api.put(`/profile/addresses/${editingId}`, addressForm);
        toast.success("تم تعديل العنوان");
      } else {
        await api.post("/profile/addresses", addressForm);
        toast.success("تم حفظ العنوان");
      }
      resetAddressForm();
      await Promise.all([loadAddresses(), refreshUser()]);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSavingAddress(false);
    }
  };

  const editAddress = (item) => {
    setActiveSection("addresses");
    setAddressFormOpen(true);
    setEditingId(item.id);
    setAddressForm({
      label: item.label,
      address: item.address,
      is_default: Boolean(item.is_default),
      lat: item.lat ?? null,
      lng: item.lng ?? null,
    });
    document.getElementById("address-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const deleteAddress = async (item) => {
    if (!window.confirm(`حذف عنوان «${item.label}»؟`)) return;
    try {
      await api.delete(`/profile/addresses/${item.id}`);
      await Promise.all([loadAddresses(), refreshUser()]);
      if (editingId === item.id) resetAddressForm();
      toast.success("تم حذف العنوان");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const makeDefault = async (item) => {
    try {
      await api.put(`/profile/addresses/${item.id}`, { ...item, is_default: true });
      await Promise.all([loadAddresses(), refreshUser()]);
      toast.success(`أصبح «${item.label}» عنوانك الافتراضي`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("تحديد الموقع غير مدعوم في هذا الجهاز");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setAddressForm((current) => ({ ...current, lat: coords.latitude, lng: coords.longitude }));
        setLocating(false);
        toast.success("تم ربط موقعك بهذا العنوان");
      },
      () => {
        setLocating(false);
        toast.error("تعذر تحديد موقعك، تأكد من السماح بالوصول للموقع");
      },
      { timeout: 8000, maximumAge: 60000 }
    );
  };

  const initial = user?.name?.trim()?.charAt(0) || "ك";
  const fieldClass = "h-12 rounded-[16px] border-0 bg-muted px-4 shadow-none focus-visible:ring-2 focus-visible:ring-primary/20";

  return (
    <div data-testid="profile-page" className="mx-auto max-w-6xl">
      <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary">حسابك وعناوينك</span>
          <h1 className="mt-1 text-3xl font-extrabold tracking-[-0.03em] sm:text-4xl">الملف الشخصي</h1>
          <p className="mt-2 text-sm text-muted-foreground">حدّث معلوماتك واحفظ عناوينك لتطلب أسرع في كل مرة.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => { logout(); navigate("/"); }}
          className="h-11 self-start rounded-full px-5 text-destructive hover:bg-destructive/5 hover:text-destructive sm:self-auto"
        >
          <LogOut className="ml-2 h-4 w-4" />
          تسجيل الخروج
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-2 rounded-2xl bg-muted p-1 md:hidden" role="tablist" aria-label="أقسام الملف الشخصي">
        <button type="button" role="tab" aria-selected={activeSection === "profile"} onClick={() => setActiveSection("profile")} className={`min-h-10 rounded-xl text-xs font-extrabold ${activeSection === "profile" ? "bg-card text-primary shadow-sm" : "text-muted-foreground"}`}>معلومات الحساب</button>
        <button type="button" role="tab" aria-selected={activeSection === "addresses"} onClick={() => setActiveSection("addresses")} className={`min-h-10 rounded-xl text-xs font-extrabold ${activeSection === "addresses" ? "bg-card text-primary shadow-sm" : "text-muted-foreground"}`}>عناويني ({addresses.length})</button>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,.72fr)_minmax(0,1.28fr)] lg:gap-8">
        <section className={`${activeSection === "profile" ? "block" : "hidden"} rounded-[30px] bg-card p-5 shadow-card sm:p-6 md:block lg:sticky lg:top-24`}>
          <div className="mb-6 flex items-center gap-4">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-[22px] bg-brand text-2xl font-extrabold text-brand-foreground shadow-brand">
              {initial}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-extrabold">{user?.name}</h2>
              <p className="mt-1 truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          {user?.phone_verified === false && (
            <div className="mb-5 rounded-[20px] border border-amber-500/20 bg-amber-500/[0.06] p-4">
              <p className="text-sm font-extrabold">وثّق رقم WhatsApp قبل تأكيد طلبك</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {user.phone ? "أكمل التحقق من الرقم المسجل لحماية حسابك." : "أضف رقم WhatsApp السوري لاستلام رمز التحقق."}
              </p>
              <Button
                type="button"
                onClick={() => navigate(`/auth?mode=${user.phone ? "verify" : "change-phone"}`)}
                className="mt-3 h-10 rounded-full px-4 text-xs font-extrabold"
              >
                {user.phone ? "تأكيد الرقم" : "إضافة رقم WhatsApp"}
              </Button>
            </div>
          )}

          <form onSubmit={saveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile-name" className="text-xs font-extrabold">الاسم الكامل</Label>
              <div className="relative">
                <UserRound className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="profile-name" data-testid="profile-name" value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} className={`${fieldClass} pr-11`} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-phone" className="text-xs font-extrabold">رقم الهاتف</Label>
              <div className="relative">
                <Phone className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="profile-phone" data-testid="profile-phone" type="tel" dir="ltr" value={profile.phone} readOnly className={`${fieldClass} pr-11 text-start opacity-70`} />
              </div>
              <p className="text-xs text-muted-foreground">رقم WhatsApp المستخدم للتحقق من الحساب.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-email" className="text-xs font-extrabold">البريد الإلكتروني</Label>
              <div className="relative">
                <Mail className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="profile-email" value={user?.email || ""} disabled dir="ltr" className={`${fieldClass} pr-11 text-start opacity-70`} />
              </div>
            </div>
            <Button type="submit" disabled={savingProfile} className="h-12 w-full rounded-full font-extrabold">
              {savingProfile ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="ml-2 h-4 w-4" />}
              حفظ المعلومات
            </Button>
          </form>
        </section>

        <div className={`${activeSection === "addresses" ? "block" : "hidden"} space-y-6 md:block`}>
          <section className="rounded-[30px] bg-card p-5 shadow-card sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold">عناويني المحفوظة</h2>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">اختر أي عنوان منها مباشرة عند إتمام طلبك.</p>
              </div>
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-brand text-brand-foreground">
                <MapPin className="h-5 w-5" />
              </div>
            </div>
            <Button
              type="button"
              onClick={() => { setAddressFormOpen(true); setEditingId(null); setAddressForm(emptyAddress); }}
              className="mt-4 h-11 w-full rounded-2xl font-extrabold md:hidden"
            >
              <Plus className="ml-2 h-4 w-4" /> إضافة عنوان
            </Button>

            {loadingAddresses ? (
              <div className="grid min-h-32 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : addresses.length === 0 ? (
              <div className="mt-5 rounded-[22px] bg-muted p-6 text-center">
                <MapPin className="mx-auto h-7 w-7 text-muted-foreground" />
                <p className="mt-3 text-sm font-bold">لا يوجد عنوان محفوظ بعد</p>
                <p className="mt-1 text-xs text-muted-foreground">أضف البيت أو المحل ولن تحتاج لكتابته في كل طلب.</p>
              </div>
            ) : (
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {addresses.map((item) => (
                  <article key={item.id} className={`relative rounded-[22px] border p-4 transition-colors ${item.is_default ? "border-primary/35 bg-primary/[0.045]" : "border-border bg-background"}`}>
                    <div className="flex items-start gap-3">
                      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl ${item.is_default ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                        <AddressIcon label={item.label} className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-extrabold">{item.label}</h3>
                          {item.is_default && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-extrabold text-primary">الافتراضي</span>}
                        </div>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">{item.address}</p>
                        {item.lat != null && <p className="mt-1 flex items-center gap-1 text-[10px] text-primary"><Navigation className="h-3 w-3" /> مرتبط بالموقع</p>}
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 border-t pt-3">
                      {!item.is_default && (
                        <button onClick={() => makeDefault(item)} className="ml-auto text-[10px] font-extrabold text-primary hover:underline">تعيين كافتراضي</button>
                      )}
                      <button aria-label={`تعديل ${item.label}`} onClick={() => editAddress(item)} className="grid h-8 w-8 place-items-center rounded-full bg-muted text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                      <button aria-label={`حذف ${item.label}`} onClick={() => deleteAddress(item)} className="grid h-8 w-8 place-items-center rounded-full bg-destructive/5 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section id="address-form" className={`${addressFormOpen ? "block" : "hidden"} rounded-[30px] bg-card p-5 shadow-card sm:p-6 md:block`}>
            <div className="mb-5 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary/10 text-primary">
                {editingId ? <Pencil className="h-4 w-4" /> : <Plus className="h-5 w-5" />}
              </div>
              <div>
                <h2 className="font-extrabold">{editingId ? "تعديل العنوان" : "إضافة عنوان جديد"}</h2>
                <p className="mt-0.5 text-[10px] text-muted-foreground">سمّه «البيت» أو «المحل» أو أي اسم يسهل تمييزه.</p>
              </div>
            </div>

            <form onSubmit={saveAddress} className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="address-label" className="text-xs font-extrabold">اسم العنوان</Label>
                <Input id="address-label" data-testid="address-label" value={addressForm.label} onChange={(event) => setAddressForm({ ...addressForm, label: event.target.value })} placeholder="مثال: البيت أو المحل" className={fieldClass} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="address-details" className="text-xs font-extrabold">تفاصيل العنوان</Label>
                <Input id="address-details" data-testid="address-details" value={addressForm.address} onChange={(event) => setAddressForm({ ...addressForm, address: event.target.value })} placeholder="الحي، الشارع، وأقرب نقطة دالة" className={fieldClass} />
              </div>
              <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAddressForm({ ...addressForm, is_default: !addressForm.is_default })}
                    className={`flex h-10 items-center gap-2 rounded-full px-4 text-xs font-extrabold transition-colors ${addressForm.is_default ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
                  >
                    <span className={`grid h-4 w-4 place-items-center rounded-full border ${addressForm.is_default ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
                      {addressForm.is_default && <Check className="h-3 w-3" />}
                    </span>
                    اجعله العنوان الافتراضي
                  </button>
                  <button type="button" onClick={useCurrentLocation} disabled={locating} className="flex h-10 items-center gap-2 rounded-full bg-muted px-4 text-xs font-extrabold text-muted-foreground hover:text-foreground">
                    {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                    {addressForm.lat != null ? "تم تحديد الموقع" : "استخدام موقعي"}
                  </button>
                </div>
                <div className="flex gap-2">
                  {editingId && <Button type="button" variant="ghost" onClick={resetAddressForm} className="h-11 rounded-full px-5">إلغاء</Button>}
                  <Button type="submit" disabled={savingAddress} className="h-11 rounded-full px-6 font-extrabold">
                    {savingAddress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="ml-2 h-4 w-4" />}
                    {editingId ? "حفظ التعديل" : "حفظ العنوان"}
                  </Button>
                </div>
              </div>
            </form>
          </section>

          <button
            type="button"
            onClick={() => navigate("/about")}
            className="flex w-full items-center gap-4 rounded-[26px] bg-accent p-5 text-start ring-1 ring-primary/10 transition-all hover:-translate-y-0.5 hover:ring-primary/25 sm:p-6"
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
              <Info className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-extrabold">معلومات البرنامج</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">اقرأ قصة بيب بيب، أهدافه، وقصة ألوانه المستوحاة من كفرنبل.</span>
            </span>
            <ArrowLeft className="h-5 w-5 shrink-0 text-primary" />
          </button>
        </div>
      </div>
    </div>
  );
}
